import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { InlineInputCardWrapper } from '@/features/editor/widgets/ai-component/inputs/shared/shared';
import {
  CatalogWidgetProps,
  LoadingEnv,
  RecommendationOptions,
  RecommendationType,
} from '@/webapi/use-widget-catalog-api';
import Portal from '@/components/portal';
import { INSPECTOR_WRAPPER_ID } from '@/features/editor/inspector';
import { LayeredRuling } from '@/features/editor/widgets/custom-widget/loading-section/layered-ruling';
import { LayeredRulingContext } from '@/features/editor/widgets/custom-widget/loading-section/layered-ruling/context';
import { useLayeredRuling } from '@/features/editor/widgets/shared/useLayeredRuling';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  CustomWidgetContext,
  CustomWidgetContextProps,
} from '@/features/editor/widgets/custom-widget/shared/context';
import { AI_SUPPORTED_UPSELL_STRATEGIES } from '@/features/editor/widgets/ai-component/inputs/shared/recs_strategies';
import { cleanRecommendationEnv } from '@/features/editor/widgets/checkout-extensibility/loading/use-checkout-recommend';
import { enrichRecsStrategy } from '@/features/editor/widgets/ai-component/inputs/shared/enrich-recs-strategy';
import { useConditionalQueryBuilder } from '@/features/editor/widgets/custom-widget/loading-section/conditional-product/useConditionalQueryBuilder';
import { useQueryBuilder } from '@/features/editor/widgets/custom-widget/loading-section/util/useQueryBuilder';
import { formatQueryBuilderConditions } from '@/features/editor/widgets/ai-component/utils/strategy-filter-formatter';
import { useAiComponentContext } from '@/features/editor/widgets/ai-component/context';
import { AiComponentTab } from '@/features/editor/widgets/ai-component/models';

export interface GenericJsRecsStrategyInputProps {
  value: string;
  onChange: (value: string) => void;
  variableName: string;
}

interface InnerRecsStrategyInputProps extends GenericJsRecsStrategyInputProps {
  formattedStrategy?: string;
  mockWidgetSchema?: CatalogWidgetProps;
  onRecOverlayClosed?: () => void;
}

export function GenericJsRecsStrategyInput({
  variableName,
  value,
  onChange,
}: GenericJsRecsStrategyInputProps) {
  const { condition: qbProps } = useConditionalQueryBuilder(
    RecommendationType.ADVANCED_RULING,
  );

  const backToTab = useAiComponentContext((ctx) => ctx.backToTab);
  const selectTab = useAiComponentContext((ctx) => ctx.selectTab);

  const { condition: ruleQbProps } = useQueryBuilder(false, true);

  const parsedStrategy = React.useMemo(() => {
    try {
      const loadingEnv = JSON.parse(value) as LoadingEnv;
      return {
        recommendationOptions: {
          ...loadingEnv.recommendationOptions,
          env: 2,
          appId: `upsells`,
        },
      } as LoadingEnv;
    } catch {
      return null;
    }
  }, [value]);

  const editorStrategy = useMemo(() => {
    const loaded = enrichRecsStrategy(parsedStrategy, qbProps, ruleQbProps);
    if (loaded) return loaded;
    return parsedStrategy;
  }, [parsedStrategy]);

  const mockWidgetSchema = useMemo<CatalogWidgetProps>(
    () => ({
      settings: {
        general: {
          title: ``,
          key: ``,
          specs: [
            {
              key: `recsCount`,
              value: {
                value: parsedStrategy?.recommendationOptions?.size || 9,
              },
              metadata: {},
              type: ``,
            },
          ],
        },
        // @ts-ignore
        loading:
          {
            value: AI_SUPPORTED_UPSELL_STRATEGIES.find(
              (s) => s.value.type === RecommendationType.ADVANCED_RULING,
            ),
            loadingEnv: editorStrategy,
            options: AI_SUPPORTED_UPSELL_STRATEGIES,
            title: ``,
          } || {},
      },
    }),
    [editorStrategy],
  );

  const mockWidgetCtx = useMemo(
    () => ({
      currentSchema: mockWidgetSchema,

      currentWidget: { id: `ai-widget` },

      updateRecommendationOptions: (options: RecommendationOptions) => {
        onChange(
          JSON.stringify(
            cleanRecommendationEnv({
              recommendationOptions: { ...options, env: 2, appId: `upsells` },
            }),
          ),
        );
        backToChatIfNeeded();
      },
    }),
    [mockWidgetSchema],
  );

  const formattedStrategy = React.useMemo(() => {
    let out = ``;
    if (editorStrategy) {
      editorStrategy?.recommendationOptions?.layeredRuling?.forEach?.(
        (ruling, index) => {
          out += `${index > 0 ? `\n\n` : ``}Rule #${index + 1}: ${formatName(
            ruling.strategy,
          )}\n`;
          if (ruling?.strategyCond?.length > 0) {
            out += formatQueryBuilderConditions(ruling.strategyCond);
          }

          if (ruling.strategyPerSlot?.slots?.length > 0) {
            out += `Manual Strategy per Slot\n`;
          }
        },
      );
    }
    out = out.replaceAll(`AND`, `\nAND`);
    return out;
  }, [editorStrategy]);

  const backToChatIfNeeded = () => {
    setTimeout(() => {
      if (backToTab.value === AiComponentTab.PROMPT) {
        selectTab(AiComponentTab.PROMPT);
      }
    }, 800);
  };

  return (
    <CustomWidgetContext.Provider
      value={mockWidgetCtx as unknown as CustomWidgetContextProps}
    >
      <InnerRecsStrategyInput
        value={value}
        onChange={onChange}
        variableName={variableName}
        formattedStrategy={formattedStrategy}
        mockWidgetSchema={mockWidgetSchema}
        onRecOverlayClosed={backToChatIfNeeded}
      />
    </CustomWidgetContext.Provider>
  );
}

function InnerRecsStrategyInput({
  variableName,
  formattedStrategy,
  mockWidgetSchema,
  onRecOverlayClosed,
}: InnerRecsStrategyInputProps) {
  const {
    devicePreview: {
      editorState: { device },
    },
  } = useContext(EditorContext);

  const rulingRef = React.useRef<HTMLButtonElement>(null);
  const { showNewRuling, setShowRuling, layeredRulingCtx } = useLayeredRuling(
    device,
    mockWidgetSchema,
    false,
    true,
  );

  return (
    <InlineInputCardWrapper>
      <RulesContainer>
        <span>Recommendations Rules:</span>
        <Description>{formattedStrategy}</Description>
      </RulesContainer>
      <EditButton
        id={`edit-strategy-${variableName}`}
        ref={rulingRef}
        onClick={() => setShowRuling(true)}
      >
        Edit
      </EditButton>
      <Portal selector={INSPECTOR_WRAPPER_ID}>
        <LayeredRulingContext.Provider value={layeredRulingCtx}>
          <LayeredRuling
            isOpen={showNewRuling}
            fromRef={rulingRef}
            close={() => {
              setShowRuling(false);
              onRecOverlayClosed?.();
            }}
          />
        </LayeredRulingContext.Provider>
      </Portal>
    </InlineInputCardWrapper>
  );
}

function formatName(name: string) {
  const formatted =
    name
      ?.replace(/[_\-]+/g, ` `) // underscores/hyphens to spaces
      ?.replace(/([a-z])([A-Z])/g, `$1 $2`) // camelCase to spaces
      ?.replace(/\s+/g, ` `) // collapse multiple spaces
      ?.trim() || ``;

  return formatted
    .split(` `)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(` `);
}

const RulesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Description = styled.div`
  font-size: 1.2rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const EditButton = styled.button`
  border: none;
  appearance: none;
  outline: none;
  background: #3451fa;
  border-radius: 1rem;
  color: white;
  padding: 0.5rem 1.5rem;
  cursor: pointer;

  font-size: 1.3rem;
  font-weight: 500;

  transition: background 0.2s ease-in-out;

  &:hover {
    background: #243ed9;
  }

  &:active {
    background: #192fb7;
  }
`;
